/**
 * /tracking/first-party-cookie — first-party cookie tracking operator UI
 * (feature #164).
 *
 * Declares a TanStack Router route and renders `FirstPartyCookiePage`. The
 * root-owned `router.tsx` wires this route into the tree during the wire-up
 * cycle that follows the prod merge; agents never touch `router.tsx`.
 *
 * Search params:
 *   - `profile_id` — deep-link a specific profile's detail card into focus.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { FirstPartyCookiePage } from "@/features/first-party-cookie/FirstPartyCookiePage";

// ---------------------------------------------------------------------------
// Route — mounts at `/tracking/first-party-cookie`.
// ---------------------------------------------------------------------------

interface FirstPartyCookieSearch {
	profile_id?: string;
}

export const trackingFirstPartyCookieRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/first-party-cookie",
	validateSearch: (raw: Record<string, unknown>): FirstPartyCookieSearch => {
		const out: FirstPartyCookieSearch = {};
		if (typeof raw.profile_id === "string" && raw.profile_id.length > 0) {
			out.profile_id = raw.profile_id;
		}
		return out;
	},
	component: TrackingFirstPartyCookieRoute,
});

export default trackingFirstPartyCookieRoute;

export const TRACKING_FIRST_PARTY_COOKIE_PATH = "/tracking/first-party-cookie";

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + heading + live feature component.
// ---------------------------------------------------------------------------

export function TrackingFirstPartyCookieRoute() {
	const { profile_id } = trackingFirstPartyCookieRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<FirstPartyCookiePage initialProfileId={profile_id} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">First-party cookie</li>
			</ol>
		</nav>
	);
}
